/**
 * Shared metadata for Kerala boat race blog posts (EN / Malayalam pairs).
 */

export const BOAT_RACE_CATEGORY = 'boat-races';

export const BOAT_RACE_LOCALES = {
  en: { id: 'en', label: 'English', shortLabel: 'EN', nativeLabel: 'English' },
  ml: { id: 'ml', label: 'Malayalam', shortLabel: 'ML', nativeLabel: 'മലയാളം' },
};

export const BOAT_RACE_TOPICS = {
  guide: {
    id: 'guide',
    labelEn: 'NTBR history & visitor guide',
    labelMl: 'നെഹ്‌റു ട്രോഫി ചരിത്രവും ഗൈഡും',
  },
  'chundan-2026': {
    id: 'chundan-2026',
    labelEn: '2026 chundan boats, clubs & captains',
    labelMl: '2026 ചുണ്ടൻ വള്ളങ്ങൾ, ക്ലബുകൾ, കാപ്റ്റൻമാർ',
  },
  'thalavady-chundan': {
    id: 'thalavady-chundan',
    labelEn: 'Thalavady Chundan — history & 2026 season',
    labelMl: 'തലവടി ചുണ്ടൻ — ചരിത്രവും 2026 സീസണും',
  },
  streaming: {
    id: 'streaming',
    labelEn: 'Free live stream & YouTube channels',
    labelMl: 'സൗജന്യ ലൈവ് സ്ട്രീമിംഗ് & YouTube ചാനലുകൾ',
  },
  'upper-kuttanad-2026': {
    id: 'upper-kuttanad-2026',
    labelEn: 'Upper Kuttanad Vallam Kali 2026 — timing, boats & live stream',
    labelMl: 'അപ്പർ കുട്ടനാട് വള്ളംകളി 2026 — സമയം, വള്ളങ്ങൾ & ലൈവ്',
  },
};

/**
 * Free YouTube channels that regularly carry Kerala vallamkali / NTBR live or highlights.
 * Descriptions are English; Malayalam post uses translated copy in its post file.
 */
export const BOAT_RACE_STREAMING_CHANNELS_PUBLIC = [
  {
    title: 'DD Malayalam Live',
    channel: 'DD Malayalam (Doordarshan)',
    description:
      'Official DD4 Malayalam YouTube — primary public-broadcaster telecast every NTBR season (heats from ~2:00 PM IST, chundan final in the afternoon block).',
    url: 'https://www.youtube.com/@DoordarshanMalayalam',
  },
  {
    title: 'Doordarshan National',
    channel: 'DD National',
    description:
      'National Doordarshan feed — has carried Nehru Trophy live streams from Punnamada in recent editions alongside DD Malayalam.',
    url: 'https://www.youtube.com/@DoordarshanNational',
  },
];

export const BOAT_RACE_STREAMING_CHANNELS_NEWS = [
  {
    title: 'Asianet News Live',
    channel: 'Asianet News',
    description: 'Major Kerala news network — NTBR live streams, Malayalam commentary, and same-day highlight reels.',
    url: 'https://www.youtube.com/@asianetnews',
  },
  {
    title: 'Manorama News Live',
    channel: 'Manorama News',
    description: 'Malayala Manorama news channel — full race-day coverage and chundan final highlights on YouTube.',
    url: 'https://www.youtube.com/@manoramanews',
  },
  {
    title: '24 News Live',
    channel: '24 News (Twenty Four)',
    description:
      'Twenty Four News — Shaiju Damodaran and Iype Vallikadan have led NTBR commentary on this feed in recent seasons.',
    url: 'https://www.youtube.com/@24onlive',
  },
  {
    title: 'Mathrubhumi Live',
    channel: 'Mathrubhumi News',
    description: 'Mathrubhumi News live YouTube desk — NTBR windows and vallamkali season updates.',
    url: 'https://www.youtube.com/@mathrubhuminewslive',
  },
  {
    title: 'Kairali News Live',
    channel: 'Kairali News',
    description: 'Kairali TV news wing — Onam-season boat race live streams and bank-side reports.',
    url: 'https://www.youtube.com/@KairaliNews',
  },
  {
    title: 'MediaOne Live',
    channel: 'MediaOne',
    description: 'MediaOne Malayalam news — NTBR and regional vallamkali coverage through the monsoon calendar.',
    url: 'https://www.youtube.com/@MediaOneTVLive',
  },
  {
    title: 'News18 Kerala Live',
    channel: 'News18 Kerala',
    description: 'Network18’s Kerala feed — race-day live YouTube streams and post-race analysis.',
    url: 'https://www.youtube.com/@News18Kerala',
  },
  {
    title: 'Reporter LIVE',
    channel: 'Reporter TV',
    description: 'Reporter network live YouTube feed — covers major Kerala boat races including Nehru Trophy weekend.',
    url: 'https://www.youtube.com/@reporterlive',
  },
  {
    title: 'Janam TV Live',
    channel: 'Janam Media',
    description: 'Janam TV official YouTube — NTBR and other vallamkali events when scheduled on the network.',
    url: 'https://www.youtube.com/@JanamTVChannel',
  },
  {
    title: 'Raj News Malayalam',
    channel: 'Raj News Malayalam',
    description: 'Raj Television Malayalam news — regional race coverage and highlight packages.',
    url: 'https://www.youtube.com/@RajNewsMalayalamOnline',
  },
];

export const BOAT_RACE_STREAMING_CHANNELS_TOURISM = [
  {
    title: 'Champions Boat League',
    channel: 'CBL — Kerala Tourism',
    description:
      'Official Champions Boat League channel — NTBR as league opener plus CBL venue races across Kerala (highlights and live when broadcast).',
    url: 'https://www.youtube.com/@ChampionsBoatLeague',
  },
  {
    title: 'Kerala Tourism',
    channel: 'Kerala Tourism',
    description: 'State tourism board — promotional films, race calendars, and CBL / vallamkali highlight reels.',
    url: 'https://www.youtube.com/@KeralaTourism',
  },
];

export const BOAT_RACE_IMAGES = {
  hero: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/80/NehruTrophyBoatRace2024.jpg/1280px-NehruTrophyBoatRace2024.jpg',
  heats:
    'https://upload.wikimedia.org/wikipedia/commons/thumb/8/80/NehruTrophyBoatRace2024.jpg/1280px-NehruTrophyBoatRace2024.jpg',
  alleppey:
    'https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Nehru_trophy_boat_race%2C_Alleppey_%289488357610%29.jpg/1280px-Nehru_trophy_boat_race%2C_Alleppey_%289488357610%29.jpg',
  aranmula:
    'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4f/Kerala_boatrace.jpg/1280px-Kerala_boatrace.jpg',
  boats:
    'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4f/Kerala_boatrace.jpg/1280px-Kerala_boatrace.jpg',
};

export const BOAT_RACE_REFERENCE_LINKS_EN = [
  { label: 'Official Nehru Trophy Boat Race website', url: 'https://nehrutrophy.nic.in/' },
  {
    label: 'About NTBR — Nehru Trophy Boat Race Society',
    url: 'https://nehrutrophy.nic.in/pages-en-IN/about-ntbr.php',
  },
  { label: 'Online tickets for NTBR 2026', url: 'https://nehrutrophy.nic.in/pages-en-IN/online_ticket.php' },
  { label: 'Alappuzha District — Nehru Trophy Boat Race', url: 'https://alappuzha.nic.in/en/nehru-trophy-boat-race/' },
  { label: 'Kerala Tourism — boat races', url: 'https://www.keralatourism.org/event/boat-races/' },
];

export const BOAT_RACE_REFERENCE_LINKS_ML = [
  { label: 'നെഹ്‌റു ട്രോഫി ഔദ്യോഗിക വെബ്സൈറ്റ്', url: 'https://nehrutrophy.nic.in/' },
  { label: 'ഓൺലൈൻ ടിക്കറ്റ് — NTBR 2026', url: 'https://nehrutrophy.nic.in/pages-en-IN/online_ticket.php' },
  { label: 'ട്രാക്ക് & ഹീറ്റ് ഷെഡ്യൂൾ', url: 'https://nehrutrophy.nic.in/pages-en-IN/track.php' },
  {
    label: 'The Hindu — 2026 തയ്യാറെടുപ്പ്',
    url: 'https://www.thehindu.com/news/national/kerala/boat-clubs-in-kerala-shift-into-high-gear-as-kuttanad-readies-for-prestigious-nehru-trophy-boat-race/article71271895.ece',
  },
  { label: 'ആലപ്പുഴ ജില്ല — നെഹ്‌റു ട്രോഫി', url: 'https://alappuzha.nic.in/en/nehru-trophy-boat-race/' },
];

/** @param {object} post */
export function isBoatRacePost(post) {
  return post?.blogCategory === BOAT_RACE_CATEGORY || Boolean(post?.boatRaceTopic);
}

/** @param {object} post */
export function getBoatRaceLocale(post) {
  return post?.locale === 'ml' ? BOAT_RACE_LOCALES.ml : BOAT_RACE_LOCALES.en;
}
